import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

const QR_SIZE = 148;
const TAIL_W = 22;
const TAIL_H = 12;

/** Thin L-brackets framing the QR (scan aesthetic) */
function QrFrameBrackets({ size }) {
  const len = 22;
  const thick = 2;
  const inset = 6;
  const s = size;
  return (
    <svg
      className="pointer-events-none absolute text-black"
      width={size}
      height={size}
      style={{ top: 0, left: 0 }}
      aria-hidden
    >
      {/* top-left */}
      <path
        d={`M ${inset + len} ${inset} L ${inset} ${inset} L ${inset} ${inset + len}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={thick}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* top-right */}
      <path
        d={`M ${s - inset - len} ${inset} L ${s - inset} ${inset} L ${s - inset} ${inset + len}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={thick}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* bottom-left */}
      <path
        d={`M ${inset} ${s - inset - len} L ${inset} ${s - inset} L ${inset + len} ${s - inset}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={thick}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* bottom-right */}
      <path
        d={`M ${s - inset} ${s - inset - len} L ${s - inset} ${s - inset} L ${s - inset - len} ${s - inset}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={thick}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ShareModal = ({ isOpen, onClose, shareUrl }) => {
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    if (isOpen) setCopied(false);
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', shareUrl);
    }
  };

  const handleDownloadQr = () => {
    const qrCanvas = qrRef.current;
    if (!qrCanvas) return;

    const scale = 3;
    const qrPx = QR_SIZE * scale;
    // Only shrink the QR drawing (keep all other layout the same)
    const qrRenderScale = 0.86;
    const qrRenderPx = Math.round(qrPx * qrRenderScale);
    // White bubble padding around the *bracket box* (match reference spacing)
    const whitePad = 16 * scale;
    const whiteTopR = 18 * scale;
    const outerR = 22 * scale;
    const tailHalf = (TAIL_W / 2) * scale;
    const tailH = TAIL_H * scale;
    const bottomBarH = 24 * scale;
    const sideMargin = 6 * scale;

    // Brackets are drawn on a box slightly larger than the QR
    const bracketPad = 12 * scale;
    const bracketBox = qrPx + bracketPad * 2;

    const innerW = bracketBox + whitePad * 2;
    const whiteContentH = whitePad + bracketBox + whitePad;
    const whiteH = whiteContentH + tailH;
    const totalWidth = sideMargin * 2 + innerW;
    const totalHeight = sideMargin + whiteH + bottomBarH + sideMargin;

    const canvas = document.createElement('canvas');
    canvas.width = totalWidth;
    canvas.height = totalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const drawRoundedRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    const ox = sideMargin;
    const oy = sideMargin;
    const wx = ox;
    const wy = oy;
    const ww = innerW;
    const wb = wy + whiteContentH;

    drawRoundedRect(0, 0, totalWidth, totalHeight, outerR);
    ctx.fillStyle = '#000000';
    ctx.fill();

    const cx = wx + ww / 2;
    const whiteBottomR = 18 * scale;

    ctx.beginPath();
    ctx.moveTo(wx + whiteTopR, wy);
    ctx.lineTo(wx + ww - whiteTopR, wy);
    ctx.quadraticCurveTo(wx + ww, wy, wx + ww, wy + whiteTopR);
    // Bottom-right rounded corner before the tail
    ctx.lineTo(wx + ww, wb - whiteBottomR);
    ctx.quadraticCurveTo(wx + ww, wb, wx + ww - whiteBottomR, wb);
    ctx.lineTo(cx + tailHalf, wb);
    ctx.lineTo(cx, wb + tailH);
    ctx.lineTo(cx - tailHalf, wb);
    // Bottom-left rounded corner after the tail
    ctx.lineTo(wx + whiteBottomR, wb);
    ctx.quadraticCurveTo(wx, wb, wx, wb - whiteBottomR);
    ctx.lineTo(wx, wy + whiteTopR);
    ctx.quadraticCurveTo(wx, wy, wx + whiteTopR, wy);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    // Thick black border around the white speech-bubble panel (incl. tail)
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 10 * scale;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();

    // QR
    const qrDrawX = wx + whitePad + bracketPad;
    const qrDrawY = wy + whitePad + bracketPad;
    const qrDx = qrDrawX + (qrPx - qrRenderPx) / 2;
    const qrDy = qrDrawY + (qrPx - qrRenderPx) / 2;
    ctx.drawImage(qrCanvas, qrDx, qrDy, qrRenderPx, qrRenderPx);

    // L-brackets (same inset logic as DOM)
    // Draw brackets on a box slightly larger than QR (so they sit outside it)
    const inset = 10 * scale;
    const len = 34 * scale;
    const thick = 4 * scale;
    const bx = wx + whitePad;
    const by = wy + whitePad;
    const bw = bracketBox;
    const bh = bracketBox;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = thick;
    // Brackets should have sharp corners (no rounding)
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';

    const drawL = (x1, y1, x2, y2, x3, y3) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x3, y3);
      ctx.stroke();
    };

    drawL(bx + inset + len, by + inset, bx + inset, by + inset, bx + inset, by + inset + len);
    drawL(bx + bw - inset - len, by + inset, bx + bw - inset, by + inset, bx + bw - inset, by + inset + len);
    drawL(bx + inset, by + bh - inset - len, bx + inset, by + bh - inset, bx + inset + len, by + bh - inset);
    drawL(
      bx + bw - inset,
      by + bh - inset - len,
      bx + bw - inset,
      by + bh - inset,
      bx + bw - inset - len,
      by + bh - inset
    );

    // Footer: logo + URL
    const footerY = oy + whiteH;
    const logo = new window.Image();
    logo.crossOrigin = 'anonymous';
    logo.onload = () => {
      // Padding from outer black card edge (not from inner white bubble)
      const padX = 16 * scale;
      const logoH = 24 * scale;
      const logoAspect = logo.naturalWidth / logo.naturalHeight;
      const logoW = logoH * logoAspect;
      const barY = footerY + (bottomBarH - logoH) / 2;

      ctx.drawImage(logo, padX, barY, logoW, logoH);

      ctx.font = `500 ${12 * scale}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'right';
      ctx.fillText('www.bloomiq.io', totalWidth - padX, footerY + bottomBarH / 2);

      const link = document.createElement('a');
      link.download = 'bloom-biq-qr.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    logo.onerror = () => {
      ctx.font = `600 ${14 * scale}px system-ui, sans-serif`;
      ctx.fillStyle = '#f97316';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText('Bloom', 16 * scale, footerY + bottomBarH / 2);
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'right';
      ctx.fillText('www.bloomiq.io', totalWidth - 16 * scale, footerY + bottomBarH / 2);
      const link = document.createElement('a');
      link.download = 'bloom-biq-qr.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    logo.src = '/assets/logo.png';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative bg-[#1d1d1d] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#828382] hover:text-[#f0f5f2] transition-colors z-10"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header: centered logo (no text) */}
        <div className="flex justify-center mb-5">
          <img
            src="/assets/logo.png"
            alt="Bloom"
            width={44}
            height={44}
            className="w-10 h-12 object-contain"
          />
        </div>

        {/* Modal preview (keep this simple; the downloaded image is styled separately) */}
        <div className="flex flex-col items-center gap-3 mb-5">
          <div className="bg-white rounded-2xl p-4">
            <QRCodeCanvas ref={qrRef} value={shareUrl} size={220} />
          </div>
          <button
            type="button"
            onClick={handleDownloadQr}
            className="flex items-center gap-1.5 text-sm text-[#828382] hover:text-[#f0f5f2] transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download QR
          </button>
        </div>

        <div className="flex items-center gap-2 bg-[#282a29] rounded-lg px-3 py-2">
          <span className="flex-1 text-sm text-[#828382] truncate">{shareUrl}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 px-3 py-1.5 bg-bloom-green hover:bg-bloom-green-hover text-[#f0f5f2] text-sm font-medium rounded-md transition-colors"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { ShareModal };
export default ShareModal;
